import amplitude from 'amplitude-js';
import { isElectron } from '@/helpers/global-helpers';
import Bowser from 'bowser';
import { ANALYTICS_FEATURE_FLAGS } from '@/consts/global-consts';

const appKey = process.env.VUE_APP_AMPLITUDE_KEY;
let isInitialized = false;
let isAppcuesEnabled = false;
let userIdentify = null;
let sessionId;
let meetingName;
let domainType;

// Adobe Customer Journey Analytics schema requires:
// 1. All keys to be lowercase
// 2. All keys to use underscores instead of spaces
// 3. All values to be strings
// 4. If a value is null or undefined, it must be replaced with an empty string
const convertPropsToAdobeSchema = (arr) => {
  return arr.reduce((acc, item) => {
    let key, value;

    // Determine if the item is a key-value pair in array format (e.g., ['Key Name', 'value']
    if (Array.isArray(item)) {
      [key, value] = item;
      // Or if it's an object with explicit `key` and `value` fields, as the initialization values (e.g. {"key":"User Role","value":"example"})
    } else if (item && typeof item === 'object') {
      ({ key, value } = item);
    }

    let processedValue = value;
    if (typeof value === 'boolean' || typeof value === 'number') {
      processedValue = value.toString();
    }

    const newKey = key.replace(/\s/g, '_').toLowerCase();
    acc[newKey] = processedValue || '';

    return acc;
  }, {});
};

const getParsedUAProps = () => {
  let props = {};
  try {
    const ua = window.navigator.userAgent;
    if (ua) {
      const browser = Bowser.getParser(ua);

      if (browser) {
        if (browser.getOS()) {
          props['PC OS Version'] = `${browser.getOS().name} ${
            browser.getOS().versionName
          }`;
        }
        if (!isElectron() && browser.getBrowser()) {
          props['Web Version'] = `${browser.getBrowser().name} ${
            browser.getBrowser().version
          }`;
        }
      }
    }
  } catch (err) {
    // ignored
  }

  return props;
};

const UAprops = getParsedUAProps();
let initializedAdobeProps = {};

export default {
  init(userId, userProperties = [], groupProperties = []) {
    if (isInitialized) {
      return;
    }

    if (ANALYTICS_FEATURE_FLAGS.ADOBE) {
      userId ??= '';
      initializedAdobeProps = convertPropsToAdobeSchema([
        { key: 'user_id', value: userId }, // Adobe Analytics expects user_id to be included in the props
        ...groupProperties,
        ...userProperties
      ]);
    }

    if (ANALYTICS_FEATURE_FLAGS.AMPLITUDE) {
      const instance = amplitude.getInstance();
      instance.init(appKey, null);
      instance.clearUserProperties();
      instance.setUserId(userId);
      instance.regenerateDeviceId();
      userIdentify = new amplitude.Identify();
      userProperties.forEach((prop) => userIdentify.set(prop.key, prop.value));
      instance.identify(userIdentify);
      groupProperties.forEach((prop) =>
        instance.setGroup(prop.key, prop.value)
      );
    }

    isInitialized = true;
  },

  enableAppcues() {
    isAppcuesEnabled = true;
  },

  setAccountId(value) {
    if (ANALYTICS_FEATURE_FLAGS.ADOBE) {
      initializedAdobeProps.account_id = value || '';
    }

    if (ANALYTICS_FEATURE_FLAGS.AMPLITUDE) {
      try {
        const instance = amplitude.getInstance();
        instance.setGroup('Account ID', value);
      } catch (e) {
        // do nothing
      }
    }
  },

  setUserId(value) {
    if (ANALYTICS_FEATURE_FLAGS.ADOBE) {
      initializedAdobeProps.user_id = value || '';
    }

    if (ANALYTICS_FEATURE_FLAGS.AMPLITUDE) {
      const instance = amplitude.getInstance();
      instance.setUserId(value);
    }
  },

  updateSessionProps(meetingId, roomName, domain) {
    sessionId = meetingId;
    meetingName = roomName;
    domainType = domain;
  },

  trackEvent(eventType, eventProps = {}) {
    try {
      const instance = amplitude.getInstance();

      const environment = window.isMobileWebSupported ? 'Mobile Web' : 'Web';

      let props = Object.assign(eventProps, UAprops, {
        Application: 'Meetings',
        'Meetings Version': process.env.VUE_APP_VERSION,
        'Meeting Name': meetingName,
        'Meeting ID': sessionId,
        Domain: domainType,
        'OS Platform': isElectron() ? 'Native' : 'Web',
        'Meetings Environment': environment
      });

      if (isElectron() && window.Electron.version) {
        props['Native Version'] = window.Electron.version;
      }

      if (ANALYTICS_FEATURE_FLAGS.ADOBE) {
        window.adobeDataLayer = window.adobeDataLayer || [];

        const convertedProps = convertPropsToAdobeSchema(Object.entries(props));

        const properties = Object.assign(convertedProps, initializedAdobeProps);
        window.adobeDataLayer.push({
          event: eventType,
          event_type: 'custom event',
          event_label: 'amplitude event',
          properties: properties
        });
      }

      if (ANALYTICS_FEATURE_FLAGS.AMPLITUDE) {
        instance.logEvent(eventType, props);
      }

      if (isAppcuesEnabled) {
        window.Appcues?.track(eventType, props);
      }
    } catch (err) {
      // do nothing
    }
  }
};
